// The little of Firestore the sync needs, behind an interface: listening to a query or one document, committing a
// batch, reading a document from the server, and turning moments into stored ones. `firestoreRemote` is the real one;
// `MemoryRemote` (memoryRemote.ts) is a server in memory that the unit tests and the demo run the real sync on.

import {
  Bytes, collection, deleteField, doc, DocumentReference, FieldPath, GeoPoint, getDocFromServer, onSnapshot, query,
  serverTimestamp, Timestamp, where, writeBatch, type DocumentData, type Firestore, type Query, type QueryDocumentSnapshot,
} from "firebase/firestore";
import { DELETE_FIELD, SERVER_TIMESTAMP, type Write } from "../core/changes";
import { isPlainObject, type Fields } from "../core/records";

/** A query on one collection: everything, or what changed after a moment, or what matches one field. */
export interface RemoteQuery {
  /** The collection's path, such as `users/{uid}/todos` or `shares`. */
  path: string;
  /** Only documents whose `modified` is later than this. */
  modifiedAfter?: Date;
  where?: { field: string; op: "==" | "array-contains"; value: string };
}

export interface RemoteDocument {
  id: string;
  /** The document's data; a server time still waiting for the server reads as null. */
  data: Fields;
}

export interface RemoteSnapshot {
  /** Added and changed documents, and those that left the query (not deletions: those are tombstones). */
  changes: { type: "added" | "modified" | "removed"; doc: RemoteDocument }[];
  /** Every document the query holds now. */
  docs: RemoteDocument[];
  fromCache: boolean;
  hasPendingWrites: boolean;
}

export interface RemoteDocumentSnapshot {
  /** Null when the document doesn't exist. */
  data: Fields | null;
  fromCache: boolean;
  hasPendingWrites: boolean;
}

export interface RemoteError {
  /** Firestore's code: "permission-denied", "unavailable", … */
  code: string;
  message: string;
}

export interface RemoteWrite {
  /** The document's path. */
  path: string;
  write: Write;
}

/** How values the browser cache can't keep as they are (a Timestamp, a GeoPoint) are kept and brought back. */
export interface StoredValueCodec {
  /** A plain stand-in for a stored value, or undefined when the value needs none. */
  freeze(value: unknown): { tag: string; value: unknown } | undefined;
  thaw(tag: string, value: unknown): unknown;
}

export interface Remote {
  listen(query: RemoteQuery, next: (snapshot: RemoteSnapshot) => void, failed: (error: RemoteError) => void): () => void;
  listenToDocument(path: string, next: (snapshot: RemoteDocumentSnapshot) => void, failed: (error: RemoteError) => void): () => void;
  /** Commits the writes as one batch. Resolves once the server took them; rejects when it refused them. */
  commit(writes: readonly RemoteWrite[]): Promise<void>;
  /** The document as the server has it now; null when it doesn't exist. */
  readFromServer(path: string): Promise<Fields | null>;
  /** A Date as a stored moment (Timestamp.fromDate). */
  toStoredMoment(date: Date): unknown;
  values: StoredValueCodec;
}

/** The error of anything that isn't a Firestore error. */
export function remoteError(error: unknown): RemoteError {
  if (typeof error === "object" && error !== null && typeof (error as { code?: unknown }).code === "string") {
    return { code: (error as { code: string }).code, message: String((error as { message?: unknown }).message ?? "") };
  }
  return { code: "unknown", message: error instanceof Error ? error.message : String(error) };
}

// MARK: - Firestore

/** The sync's view of a Firestore database. */
export function firestoreRemote(db: Firestore): Remote {
  const toDocument = (snapshot: QueryDocumentSnapshot<DocumentData>): RemoteDocument => ({ id: snapshot.id, data: snapshot.data() });
  return {
    listen(spec, next, failed) {
      let ref: Query<DocumentData> = collection(db, spec.path);
      if (spec.modifiedAfter) ref = query(ref, where("modified", ">", Timestamp.fromDate(spec.modifiedAfter)));
      if (spec.where) ref = query(ref, where(spec.where.field, spec.where.op, spec.where.value));
      return onSnapshot(ref, { includeMetadataChanges: true }, (snapshot) => next({
        changes: snapshot.docChanges().map((change) => ({ type: change.type, doc: toDocument(change.doc) })),
        docs: snapshot.docs.map(toDocument),
        fromCache: snapshot.metadata.fromCache,
        hasPendingWrites: snapshot.metadata.hasPendingWrites,
      }), (error) => failed(remoteError(error)));
    },
    listenToDocument(path, next, failed) {
      return onSnapshot(doc(db, path), { includeMetadataChanges: true }, (snapshot) => next({
        data: snapshot.exists() ? snapshot.data() : null,
        fromCache: snapshot.metadata.fromCache,
        hasPendingWrites: snapshot.metadata.hasPendingWrites,
      }), (error) => failed(remoteError(error)));
    },
    async commit(writes) {
      const batch = writeBatch(db);
      for (const { path, write } of writes) {
        const ref = doc(db, path);
        const fields = firestoreFields(write.fields) as DocumentData;
        // Each top-level field is one FieldPath, so a field whose name holds a dot stays one field.
        if (write.kind === "set") batch.set(ref, fields);
        else batch.set(ref, fields, { mergeFields: Object.keys(fields).map((key) => new FieldPath(key)) });
      }
      await batch.commit();
    },
    async readFromServer(path) {
      const snapshot = await getDocFromServer(doc(db, path));
      return snapshot.exists() ? snapshot.data() : null;
    },
    toStoredMoment: (date) => Timestamp.fromDate(date),
    values: firestoreValues(db),
  };
}

/** The core's sentinels as Firestore's: "delete this field" and "the server's time". */
function firestoreFields(value: unknown): unknown {
  if (value === DELETE_FIELD) return deleteField();
  if (value === SERVER_TIMESTAMP) return serverTimestamp();
  if (Array.isArray(value)) return value.map(firestoreFields);
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, firestoreFields(inner)]));
  return value;
}

/** Timestamps, GeoPoints, Bytes and references kept for the browser cache, so they are written back as they came. */
function firestoreValues(db: Firestore): StoredValueCodec {
  return {
    freeze(value) {
      if (value instanceof Timestamp) return { tag: "timestamp", value: [value.seconds, value.nanoseconds] };
      if (value instanceof GeoPoint) return { tag: "geopoint", value: [value.latitude, value.longitude] };
      if (value instanceof Bytes) return { tag: "bytes", value: value.toBase64() };
      if (value instanceof DocumentReference) return { tag: "reference", value: value.path };
      return undefined;
    },
    thaw(tag, value) {
      switch (tag) {
        case "timestamp": { const [seconds, nanoseconds] = value as [number, number]; return new Timestamp(seconds, nanoseconds); }
        case "geopoint": { const [latitude, longitude] = value as [number, number]; return new GeoPoint(latitude, longitude); }
        case "bytes": return Bytes.fromBase64String(value as string);
        case "reference": return doc(db, value as string);
        default: return value;
      }
    },
  };
}
